import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Camera, CheckCircle2, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { useL } from './Kit';
const API = `${process.env.REACT_APP_BACKEND_URL}/api/manifesto`;
export const evidenceUrl = id => (id ? `${API}/evidence/${id}` : null);

/** Compress to ≤1280px JPEG so tablet photos upload fast and stay under proxy limits. */
async function compress(file) {
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = URL.createObjectURL(file); });
  const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
  const c = document.createElement('canvas'); c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  URL.revokeObjectURL(img.src);
  return new Promise(res => c.toBlob(b => res(b || file), 'image/jpeg', 0.82));
}

/** Capture/pick a photo → secure upload → onUploaded(file_id). */
export default function PhotoCapture({ kind, uploader, value, onUploaded, en = 'Take photo with Tab camera', bn = 'ট্যাব ক্যামেরায় ছবি তুলুন', testId = 'photo-capture' }) {
  const { L } = useL();
  const ref = useRef();
  const [preview, setPreview] = useState(null); const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);

  const pick = async e => {
    const file = e.target.files?.[0]; if (!file) return;
    setErr(null); setBusy(true);
    try {
      const blob = await compress(file);
      setPreview(URL.createObjectURL(blob));
      const fd = new FormData(); fd.append('file', blob, 'photo.jpg'); fd.append('kind', kind || 'general'); if (uploader) fd.append('uploader', uploader);
      const r = await axios.post(`${API}/evidence/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded?.(r.data.file_id);
    } catch (ex) { setErr(ex.response?.data?.detail || L('Upload failed', 'আপলোড ব্যর্থ')); setPreview(null); onUploaded?.(null); }
    finally { setBusy(false); e.target.value = ''; }
  };
  const clear = () => { setPreview(null); onUploaded?.(null); };

  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 'var(--radius-md)', border: `1.5px dashed ${value ? 'var(--color-success)' : 'var(--color-separator)'}`, background: value ? 'var(--color-success-bg)' : 'var(--color-surface-1)' }}>
      <input ref={ref} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={pick} data-testid={`${testId}-input`} />
      {preview || value ? <img src={preview || evidenceUrl(value)} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />
        : <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--color-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ImageIcon size={22} style={{ color: 'var(--color-label-tertiary)' }} /></div>}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500 }}>{value ? L('Photo attached — stored securely', 'ছবি যুক্ত — নিরাপদে সংরক্ষিত') : L(en, bn)}</div>
        <div style={{ fontSize: 10, color: err ? 'var(--color-error)' : 'var(--color-label-tertiary)' }}>{err || L('Compressed on-device · encrypted object storage · time-stamped', 'ডিভাইসে সংকুচিত · এনক্রিপ্টেড স্টোরেজ · সময়-চিহ্নিত')}</div>
      </div>
      {busy ? <Loader2 size={18} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
        : value ? <><CheckCircle2 size={18} style={{ color: 'var(--color-success)' }} /><button type="button" data-testid={`${testId}-clear`} onClick={clear} className="btn btn-sm btn-ghost" style={{ padding: 6 }}><X size={14} /></button></>
        : <button type="button" data-testid={`${testId}-btn`} onClick={() => ref.current?.click()} className="btn btn-sm btn-primary" style={{ gap: 6 }}><Camera size={14} /> {L('Capture', 'ছবি')}</button>}
    </div>
  );
}

/** Small thumbnail with click-to-enlarge. */
export function EvidenceThumb({ id, size = 40, testId }) {
  const [open, setOpen] = useState(false);
  if (!id) return null;
  return (<>
    <img data-testid={testId} src={evidenceUrl(id)} alt="evidence" onClick={e => { e.stopPropagation(); setOpen(true); }} style={{ width: size, height: size, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: '1px solid var(--color-separator)' }} />
    {open && <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}><img src={evidenceUrl(id)} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 }} /></div>}
  </>);
}
